import {
  IconUsers,
  IconBrain,
  IconMessages,
  IconVideo,
  IconFileText,
  IconBolt,
  IconSettings,
  IconRocket,
  IconApi,
  IconCode,
  IconDatabase,
  IconRefresh,
  IconTable,
  IconLock,
  IconMessageChatbot,
  IconShieldCheck,
  IconPlugConnected,
} from "@tabler/icons-react";

/** The one MCP server every AI assistant connects to; vendor-neutral by design. */
export const MCP_SERVER_URL = "https://orgtools.com/mcp";

/**
 * Anthropic's install-link format: opens Claude's Add custom connector dialog with
 * the name and URL prefilled. The person still reviews and confirms; it does not
 * bypass their sign-in or consent.
 */
export const CLAUDE_INSTALL_URL =
  "https://claude.ai/customize/connectors?modal=add-custom-connector" +
  `&connectorName=Orgtools&connectorUrl=${encodeURIComponent(MCP_SERVER_URL)}`;

/** The one-line Claude Code setup; /mcp inside Claude Code then runs the browser sign-in. */
export const CLAUDE_CODE_ADD_COMMAND = `claude mcp add --transport http orgtools ${MCP_SERVER_URL}`;

/**
 * The Agent Skills file that walks an agent from sign-in to a verified whoami
 * call. A static file under public/, so the URL is the product domain rather
 * than a route; agents.md at the same level is a byte-identical copy.
 */
export const SKILL_URL = "https://orgtools.com/skill.md";

/**
 * The one line a person pastes into any agent that can configure its own MCP
 * client. The agent fetches the skill and follows it; the person never handles
 * a config file or a key.
 */
export const AGENT_SETUP_COMMAND = `set up ${SKILL_URL}`;

/**
 * The clients the AI assistants page walks through. The values double as the
 * Connect chapter's tab ids and as the page's URL hashes, so a #chatgpt link
 * lands on the ChatGPT tab.
 */
export const CONNECT_CLIENT = {
  CLAUDE: "claude",
  CHATGPT: "chatgpt",
  CLAUDE_CODE: "claude-code",
  OTHER: "other",
} as const;
export type ConnectClient = (typeof CONNECT_CLIENT)[keyof typeof CONNECT_CLIENT];

/** Display metadata per client; the Connect chapter's tabs derive their order from it. */
export const CONNECT_CLIENT_CONFIG: Record<ConnectClient, { label: string }> = {
  [CONNECT_CLIENT.CLAUDE]: { label: "Claude" },
  [CONNECT_CLIENT.CHATGPT]: { label: "ChatGPT" },
  [CONNECT_CLIENT.CLAUDE_CODE]: { label: "Claude Code" },
  [CONNECT_CLIENT.OTHER]: { label: "Other Agents" },
};

export const CONNECT_CLIENT_OPTIONS = (
  Object.entries(CONNECT_CLIENT_CONFIG) as Array<[ConnectClient, { label: string }]>
).map(([value, config]) => ({ value, label: config.label }));

export interface IntegrationConfig {
  id: string;
  name: string;
  /** Sentence-case band heading on the integrations index page */
  bandHeading: string;
  description: string;
  logo?: string;
  logoAlt?: string;
  benefits: Array<{
    icon: React.ComponentType<{ size?: number }>;
    text: string;
  }>;
  href: string;
  available: boolean;
  // Additional fields for detail pages
  pageTitle?: string;
  pageDescription?: string;
  valueProposition?: string;
  valuePropositionDescription?: string;
}

export const integrations: IntegrationConfig[] = [
  {
    id: "slack",
    name: "Slack",
    bandHeading: "Decisions get their own channel",
    description:
      "Create focused decision channels in Slack with real-time updates and AI-powered conversation analysis.",
    logo: "/integrations/slack-logo.png",
    logoAlt: "Slack",
    pageTitle: "Slack",
    pageDescription:
      "Create focused decision channels in Slack with real-time updates and AI-powered conversation analysis.",
    valueProposition: "Create Focused Decision Channels in Slack",
    valuePropositionDescription:
      "Stop decision discussions from getting lost in general chat threads. Create dedicated Slack channels for each decision with real-time updates, notifications, and AI-powered conversation analysis to keep stakeholders aligned and informed.",
    benefits: [
      {
        icon: IconUsers,
        text: "Create dedicated decision channels",
      },
      {
        icon: IconBrain,
        text: "AI conversation analysis",
      },
      {
        icon: IconMessages,
        text: "Real-time notifications & updates",
      },
    ],
    href: "/integrations/slack",
    available: true,
  },
  {
    id: "zoom",
    name: "Zoom",
    bandHeading: "Meetings become decisions, not notes",
    description:
      "Automatically capture and structure decisions from your Zoom meetings, eliminating manual note-taking and lost context.",
    logo: "/integrations/zoom-logo.png",
    logoAlt: "Zoom",
    pageTitle: "Zoom",
    pageDescription:
      "Automatically capture and structure decisions from your Zoom meetings, eliminating manual note-taking and lost context.",
    valueProposition: "Stop Letting Decisions Die in Meeting Notes and TODO Lists",
    valuePropositionDescription:
      "In most meetings, decisions get raised but end up as TODOs or buried in summaries. That's where they stall. Notes are static. TODOs lack context. Summaries go unread. And the actual work of making the decision discussing tradeoffs, aligning stakeholders, reaching clarity never happens. Orgtools for Zoom changes that. We automatically detect decision moments in your meetings and turn them into structured decisions with options, context, stakeholders, and clear next steps. Not just a TODO item, but a decision ready to be made.",
    benefits: [
      {
        icon: IconVideo,
        text: "Automatic decision detection",
      },
      {
        icon: IconFileText,
        text: "Context preservation",
      },
      {
        icon: IconBrain,
        text: "Structured decision processes",
      },
    ],
    href: "/integrations/zoom",
    available: true,
  },
  {
    id: "notion",
    name: "Notion",
    bandHeading: "Docs and databases become facts",
    description:
      "Share Notion pages and databases with Orgtools and they become structured facts that stay current as your team edits.",
    logo: "/integrations/notion-logo.png",
    logoAlt: "Notion",
    pageTitle: "Notion",
    pageDescription:
      "Share Notion pages and databases with Orgtools and they become structured facts that stay current as your team edits.",
    benefits: [
      {
        icon: IconRefresh,
        text: "Facts stay current as docs change",
      },
      {
        icon: IconTable,
        text: "Databases sync as structured records",
      },
      {
        icon: IconLock,
        text: "Read-only, with sensitivity controls",
      },
    ],
    href: "/integrations/notion",
    available: true,
  },
  {
    id: "zapier",
    name: "Zapier",
    bandHeading: "Six thousand apps feed the record",
    description:
      "Connect thousands of apps to automatically create decisions from external data sources using AI analysis.",
    logo: "/integrations/zapier-logo.png",
    logoAlt: "Zapier",
    pageTitle: "Zapier",
    pageDescription:
      "Connect Orgtools to thousands of apps through Zapier to automatically create decisions from external data sources.",
    valueProposition: "Automate Decision Creation from Any Data Source",
    valuePropositionDescription:
      "Transform data from your favorite apps into structured decisions automatically. Whether it's customer feedback from surveys, support tickets, or team updates, Zapier can send that information to Orgtools where AI will analyze and convert it into actionable decisions.",
    benefits: [
      {
        icon: IconBolt,
        text: "Connect 6000+ apps automatically",
      },
      {
        icon: IconSettings,
        text: "Get up-and-running in minutes",
      },
      {
        icon: IconRocket,
        text: "Transform data into decisions with AI",
      },
    ],
    href: "/integrations/zapier",
    available: true,
  },
  {
    id: "ai_assistants",
    name: "AI assistants",
    bandHeading: "Decisions made in a chat reach the record",
    description:
      "Connect Claude, ChatGPT, or Claude Code with one URL. Tell your assistant what you decided and it logs a decision in your firm's record, as your own AI seat.",
    pageTitle: "AI assistants",
    pageDescription:
      "Connect Claude, ChatGPT, or Claude Code to Orgtools with one URL. Your assistant logs decisions and reads the firm's decisions and signals under your own permissions.",
    benefits: [
      {
        icon: IconMessageChatbot,
        text: "Works with Claude, ChatGPT, and Claude Code",
      },
      {
        icon: IconShieldCheck,
        text: "Runs under your own permissions",
      },
      {
        icon: IconPlugConnected,
        text: "One URL, no keys to paste",
      },
    ],
    href: "/integrations/ai-assistants",
    available: true,
  },
  {
    id: "public_api",
    name: "API",
    bandHeading: "Build it into anything",
    description:
      "Build custom integrations using our RESTful API. Perfect for n8n, Workato, Tray.io, and other automation platforms.",
    pageTitle: "API",
    pageDescription:
      "Integrate Orgtools directly into your applications and workflows with our RESTful API. Create decisions programmatically from any system or service.",
    valueProposition: "Build Custom Integrations with Ease",
    valuePropositionDescription:
      "Connect any application, service, or workflow directly to Orgtools using our public API. Whether you're building custom automations with n8n, Workato, Tray.io, or integrating with internal tools, our API provides the flexibility you need to create decision workflows from existing systems.",
    benefits: [
      {
        icon: IconApi,
        text: "RESTful API with JSON responses",
      },
      {
        icon: IconCode,
        text: "Works with any programming language",
      },
      {
        icon: IconDatabase,
        text: "Perfect for automation platforms",
      },
    ],
    href: "/integrations/public-api",
    available: true,
  },
];

// Helper function to get integration by ID
export function getIntegrationById(id: string): IntegrationConfig | undefined {
  return integrations.find((integration) => integration.id === id);
}
